import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Clock, Mail } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { FaqAccordion, buildFaqLd } from "../components/FaqAccordion";
import { Picture, findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { BOOKING_URL, PHONE_TEL, PHONE_DISPLAY } from "../components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setPost(null);
    setError(null);
    Promise.all([
      axios.get(`${API}/blog/${slug}`),
      axios.get(`${API}/photos`),
    ])
      .then(([b, ph]) => {
        if (!alive) return;
        setPost(b.data);
        setPhotos(ph.data.photos || []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.status || "error");
      });
    return () => { alive = false; };
  }, [slug]);

  const heroPhoto = useMemo(
    () => (post ? findPhoto(photos, post.hero_photo_slug) : null),
    [post, photos],
  );

  const canonical = `https://westbridgehotels.com/blog/${slug}`;

  const jsonLd = useMemo(() => {
    if (!post) return [];
    const heroUrl = heroPhoto?.jpg
      ? `${window.location.origin}${heroPhoto.jpg}`
      : undefined;
    const article = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date_published,
      dateModified: post.date_published,
      mainEntityOfPage: canonical,
      image: heroUrl ? [heroUrl] : undefined,
      author: { "@type": "Organization", name: post.author || "Westbridge Inn & Suites Team" },
      publisher: {
        "@type": "Organization",
        name: "Westbridge Inn & Suites",
        url: "https://westbridgehotels.com",
        logo: {
          "@type": "ImageObject",
          url: `${window.location.origin}/brand/logo.png`,
        },
      },
    };
    const breadcrumbs = [
      { label: "Journal", to: "/blog" },
      { label: post.title, to: `/blog/${post.slug}` },
    ];
    const items = [
      article,
      buildBreadcrumbLd(breadcrumbs, "https://westbridgehotels.com"),
    ];
    if (post.faqs?.length) items.push(buildFaqLd(post.faqs));
    return items;
  }, [post, heroPhoto, canonical]);

  useSeo({
    title: post ? `${post.title} — Westbridge Inn & Suites` : "Journal — Westbridge Inn & Suites",
    description: post?.excerpt || "",
    canonical,
    ogType: "article",
    image: heroPhoto?.jpg ? `${window.location.origin}${heroPhoto.jpg}` : undefined,
    jsonLd,
  });

  if (error === 404) {
    return (
      <main id="main" className="min-h-[60vh] flex items-center justify-center text-center pt-32 pb-24">
        <div className="max-w-md">
          <p className="eyebrow mb-3">404</p>
          <h1 className="font-display text-3xl md:text-4xl text-forest-900 mb-4">
            That article isn&apos;t published yet.
          </h1>
          <p className="text-ink/70 mb-6">
            We may still be writing it. Head back to the journal for what&apos;s live now.
          </p>
          <Link to="/blog" className="btn-primary inline-flex" data-testid="blog-404-back">
            <ArrowLeft size={14} /> Back to the Journal
          </Link>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main id="main" className="min-h-[220vh] bg-cream pt-32" data-testid="blog-post-loading">
        <div className="container-app">
          <p className="eyebrow text-ink/40">Loading…</p>
        </div>
      </main>
    );
  }

  const breadcrumbs = [
    { label: "Journal", to: "/blog" },
    { label: post.title, to: `/blog/${post.slug}` },
  ];

  return (
    <main id="main" data-testid="blog-post-page">
      <PageHero
        eyebrow={`${post.category} · ${post.author}`}
        title={post.title}
        intro={post.excerpt}
        photo={heroPhoto}
        breadcrumbs={breadcrumbs}
        testId="blog-post-hero"
      />

      <div className="bg-cream">
        <div className="container-app py-4 flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.18em] text-ink/60 border-b border-tan/60">
          <span>{formatDate(post.date_published)}</span>
          {post.read_minutes ? (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} /> {post.read_minutes} min read
            </span>
          ) : null}
          <span className="hidden md:inline">·</span>
          <span className="hidden md:inline">Slug: /{post.slug}</span>
        </div>
      </div>

      <article className="bg-cream py-14 md:py-24">
        <div className="container-app grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* TOC */}
          {post.toc?.length ? (
            <aside className="lg:col-span-3 lg:sticky lg:top-28 lg:self-start" data-testid="blog-toc">
              <p className="eyebrow mb-4">In this piece</p>
              <ol className="space-y-2 text-sm">
                {post.toc.map((t, i) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      className="text-forest-900 hover:text-brass transition-colors flex gap-3"
                      data-testid={`toc-link-${t.id}`}
                    >
                      <span className="text-brass tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                      <span>{t.label}</span>
                    </a>
                  </li>
                ))}
              </ol>
              <div className="mt-8 pt-6 border-t border-tan">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-sm w-full justify-center"
                  data-testid="blog-toc-book"
                >
                  Book Now <ArrowUpRight size={13} />
                </a>
                <a
                  href={`tel:${PHONE_TEL}`}
                  className="mt-3 btn-ghost-dark text-sm w-full justify-center"
                  data-testid="blog-toc-call"
                >
                  Call {PHONE_DISPLAY}
                </a>
              </div>
            </aside>
          ) : null}

          {/* Body */}
          <div className={post.toc?.length ? "lg:col-span-9" : "lg:col-span-12"}>
            <div
              ref={contentRef}
              className="prose-westbridge"
              dangerouslySetInnerHTML={{ __html: post.body_html || "" }}
              data-testid="blog-body"
            />

            {post.faqs?.length ? (
              <section className="mt-16 pt-10 border-t border-tan" id="faqs">
                <p className="eyebrow mb-3">Related questions</p>
                <h2 className="font-display text-2xl md:text-3xl text-forest-900 mb-8">
                  Frequently asked questions for this piece.
                </h2>
                <FaqAccordion items={post.faqs} testIdPrefix="blog-faq" />
              </section>
            ) : null}

            <div className="mt-16 pt-10 border-t border-tan flex flex-wrap items-center gap-4">
              <Link to="/blog" className="btn-ghost-dark text-sm" data-testid="blog-back-index">
                <ArrowLeft size={13} /> All journal entries
              </Link>
              <a
                href="mailto:Centerville@westbridgehotels.com"
                className="inline-flex items-center gap-2 text-sm text-forest-900 brass-underline"
                data-testid="blog-post-email"
              >
                <Mail size={13} /> Ask the front desk
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Related */}
      {post.related?.length ? (
        <section className="bg-cream-100 paper-grain py-20 md:py-28">
          <div className="container-app">
            <p className="eyebrow mb-4">Keep reading</p>
            <h2 className="font-display font-light text-2xl md:text-4xl text-forest-900 mb-12 tracking-tight">
              More from the front desk.
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {post.related.map((r) => (
                <article
                  key={r.slug}
                  className="bg-cream-50 border border-tan hover:border-brass/70 transition-colors"
                  data-testid={`blog-related-${r.slug}`}
                >
                  <Link to={`/blog/${r.slug}`} className="block aspect-[16/10] overflow-hidden bg-forest-800">
                    <Picture
                      photo={findPhoto(photos, r.hero_photo_slug)}
                      className="block w-full h-full"
                      imgClassName="w-full h-full object-cover"
                      sizes="(min-width: 768px) 30vw, 100vw"
                    />
                  </Link>
                  <div className="p-6">
                    <p className="eyebrow mb-2">{r.category}</p>
                    <h3 className="font-display text-xl text-forest-900 leading-tight mb-3">
                      <Link to={`/blog/${r.slug}`} className="hover:text-brass transition-colors">
                        {r.title}
                      </Link>
                    </h3>
                    <p className="text-sm text-ink/75 leading-relaxed">{r.excerpt}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <CtaBlock
        eyebrow="Book direct"
        title="Ready to see it in person?"
        body="Every guide on this journal was written by people who work here. Book direct for the best rate — or call the front desk with any question."
      />
    </main>
  );
}
